import { type SelectionEvent } from '../events/types';
import { type SelectionState } from '../state/types';
import { hasActiveEdit } from '../utils/has-active-edit';

import { deselectAllReducer } from './deselect-all';
import { extendToCellReducer } from './extend-to-cell';
import { extendToColumnReducer } from './extend-to-column';
import { extendToRowReducer } from './extend-to-row';
import { fillApplyReducer } from './fill-apply';
import { fillDragReducer } from './fill-drag';
import { fillStartReducer } from './fill-start';
import { focusCellReducer } from './focus-cell';
import { previewSelectionActionReducer } from './preview-selection-action';
import { reconcileSelectionsReducer } from './reconcile-selections';
import { requestClearCellsReducer } from './request-clear-cells';
import { requestCellEditReducer } from './request-edit-cell';
import { selectCellReducer } from './select-cell';
import { selectCellRangeReducer } from './select-cell-range';
import { selectColumnsReducer } from './select-columns';
import { selectRowsReducer } from './select-rows';
import { stopEditingReducer } from './stop-editing-cell';
import { toggleReducer } from './toggle';
import { type Reducer, type SelectionReducerPolicies } from './types';

/**
 * Main reducer that routes events to their specific reducers. Add new reducers
 * here as you create them. Returns noop for unhandled events.
 */
export function createSelectionsReducer(policies: SelectionReducerPolicies): Reducer {
  return (prev: SelectionState, event: SelectionEvent) => {
    // Runs before the editing guard: Yjs / structural updates must still remap indices while editing.
    if (event.type === 'reconcileSelections') {
      return reconcileSelectionsReducer(prev, event);
    }

    // Runs before the editing guard: mouse-up always ends the fill gesture for UI. fillApply may still
    // yield noop (e.g. selections cleared by deselectAll) so fillApplied never notifies; fillEnded must
    // still reach effects so the dashed fill preview clears.
    if (event.type === 'fillEnd') {
      return {
        next: prev,
        delta: {
          kind: 'fillEnded',
          source: event.source
        }
      };
    }

    /**
     * If we are editing, the ONLY event we allow to process is 'stopEditing'
     * Everything else (clicks, API calls, keyboard nav) is strictly ignored (no-op).
     */
    if (hasActiveEdit(prev) && event.type !== 'stopEditing') {
      return {
        next: prev,
        delta: {
          kind: 'noop'
        }
      };
    }

    if (event.type === 'selectCell') {
      return selectCellReducer(prev, event);
    }

    if (event.type === 'selectCellRange') {
      return selectCellRangeReducer(prev, event);
    }

    if (event.type === 'startEditing') {
      return requestCellEditReducer(prev, event, policies);
    }

    if (event.type === 'stopEditing') {
      return stopEditingReducer(prev, event);
    }

    if (event.type === 'extendToCell') {
      return extendToCellReducer(prev, event);
    }

    if (event.type === 'selectRows') {
      return selectRowsReducer(prev, event);
    }

    if (event.type === 'extendToRow') {
      return extendToRowReducer(prev, event);
    }

    if (event.type === 'extendToColumn') {
      return extendToColumnReducer(prev, event);
    }

    if (event.type === 'toggleRow') {
      return toggleReducer(prev, {
        axis: 'row',
        primaryCoord: event.row,
        source: event.source
      });
    }

    if (event.type === 'selectColumns') {
      return selectColumnsReducer(prev, event);
    }

    if (event.type === 'toggleColumn') {
      return toggleReducer(prev, {
        axis: 'col',
        primaryCoord: event.col,
        source: event.source
      });
    }

    if (event.type === 'deselectAll') {
      return deselectAllReducer(prev, event);
    }

    if (event.type === 'previewSelectionAction') {
      return previewSelectionActionReducer(prev, event);
    }

    if (event.type === 'focusCell') {
      return focusCellReducer(prev, event);
    }

    if (event.type === 'fillStart') {
      return fillStartReducer(prev, event);
    }

    if (event.type === 'fillDrag') {
      return fillDragReducer(prev, event);
    }

    if (event.type === 'fillApply') {
      return fillApplyReducer(prev, event);
    }

    if (event.type === 'clearCells') {
      return requestClearCellsReducer(prev, event, policies);
    }

    return { next: prev, delta: { kind: 'noop' } };
  };
}
